import React, {useEffect, useState} from 'react';
import {connect} from 'react-redux';
import {compose} from 'redux';
import * as Yup from 'yup';
import {
  AppForm,
  AppFormCheckboxField,
  AppFormField,
  AppFormSubmitButton,
} from '../../../components/forms';
import * as fonts from '../../../util/fonts';
import colors from '../../../util/colors';
import './styles.scss';
import {strings} from '../../../util/strings';
import {put} from '../../../api/api';
import {uriMemberDataSubmit} from '../../../api/endpoints';
import ChangeSuccessModal from './change-success-modal';
import ModalTemplate from '../../../components/modal';
import AppButton from '../../../components/app-button';
import {useFormikContext} from 'formik';

var IBAN = require('iban');

interface IbanFilterProps {
  iBAN: string;
  updateIban: any;
  accountOwner: string;
  userFilter: any;
  currentUser: any;
  labels: typeof strings.pt.myData;
  labelsCreateRefund: typeof strings.pt.createRefund;
  labelsIbanRefunds: typeof strings.pt.ibanRefunds;
  messageLabels: typeof strings.pt.messages;
  buStyles: any;
  canSubmit?: any;
  user: any;
  toggleIbanEdition?: boolean;
  bankAccountDataHolder: any;
  selectedBu: any;
  titular: any;
}

function IbanFilter({
  iBAN,
  updateIban,
  userFilter,
  currentUser,
  labels,
  labelsCreateRefund,
  messageLabels,
  canSubmit,
  labelsIbanRefunds,
  selectedBu,
  bankAccountDataHolder,
  titular,
}: IbanFilterProps) {
  const validationSchema = Yup.object().shape({
    accountOwner: Yup.boolean()
      .required()
      .oneOf([true], messageLabels?.requiredField),

    iBAN: Yup.string()
      .required(messageLabels?.requiredField)
      .test('iban-format', messageLabels?.invalidIBAN, (value) => {
        return IBAN.isValid(value);
      }),
  });

  const formik = useFormikContext<any>();

  const [statusMessage, setStatusMessage] = useState<any>(null);
  const [formikContext, setFormikContext] = useState<any>();
  const [submitting, setSubmitting] = useState(false);
  const [hoverErrorIcon, setHoverErrorIcon] = useState({
    iban: false,
    accountOwner: false,
  });
  const [iban, setIban] = useState();
  const [validIban, setValidIban] = useState(false);
  const [errorIban, setErrorIban] = useState(false);
  const [editingIban, setEditingIban] = useState(false);
  const [justSubmitted, setJustSubmitted] = useState(false);
  const [ibanMessage, setIbanMessage] = useState('');
  const [ibanChanged, setIbanChanged] = useState(false);

  const featureName = 'EditIban';

  const isTitular =
    bankAccountDataHolder === 'SUBSCRIBER'
      ? titular?.memId === selectedBu?.memId &&
        titular?.buId === selectedBu?.buId
      : true;
  const [ibanUndefinedByDefault, setIbanUndefinedByDefault] = useState(false);

  const defineOnMouseFunctions = (
    e: React.MouseEvent,
    fieldNamePar: string
  ) => {
    var elem: HTMLElement = e.target as HTMLElement;
    var icon: HTMLElement = elem.nextSibling as HTMLElement;
    if (icon) {
      icon.onmouseover = function () {
        setHoverErrorIcon({...hoverErrorIcon, [fieldNamePar]: true});
      };
      icon.onmouseout = function () {
        setHoverErrorIcon({...hoverErrorIcon, [fieldNamePar]: false});
      };
    }
  };

  const handleSubmit = async () => {
    setSubmitting(true);

    const body = {
      buId: selectedBu.buId,
      memId: titular?.memId,
      grpId: selectedBu.grdId,
      famId: selectedBu.famId,
      grpName: selectedBu.grpName,
      multicode: titular?.memId,
      address: userFilter.addr,
      city: userFilter.city,
      zipCode: userFilter.postalcode,
      phone: userFilter.phone,
      mobilePhone: userFilter.cellPhone,
      email: userFilter.email,
      cardName: userFilter.cardName,
      iban: iban,
      username: currentUser.username,
    };

    try {
      await put(uriMemberDataSubmit(), body, featureName).then(
        (response: any) => {
          if (response?.result?.status === 'Success') {
            setStatusMessage(true);
            setEditingIban(false);
            if (iBAN !== body.iban) updateIban(body.iban); //setIbanStore
          } else {
            setErrorIban(true);
            setStatusMessage(false);
          }
        }
      );
    } catch (e: any) {
      setStatusMessage(false);
      setErrorIban(true);
    } finally {
      setSubmitting(false);
    }
  };

  const defineIbanMessage = () => {
    if (labelsIbanRefunds.ibanRefunds_ibanIsEnabled === '1') {
      if (
        labelsIbanRefunds.ibanRefunds_bankAccountDataHolder === 'SUBSCRIBER'
      ) {
        !isTitular &&
          setIbanMessage(
            !isTitular && labelsIbanRefunds.ibanRefunds_ibanEnabled_not_owner
          );
      } else if (
        labelsIbanRefunds.ibanRefunds_bankAccountDataHolder === 'INDIVIDUAL'
      ) {
        setIbanMessage(labelsIbanRefunds.ibanRefunds_ibanEnabled_owner);
      }
    } else {
      if (
        labelsIbanRefunds.ibanRefunds_bankAccountDataRequiredForRefunds ===
          '1' &&
        !iBAN
      ) {
        if (
          labelsIbanRefunds.ibanRefunds_bankAccountDataHolder === 'SUBSCRIBER'
        ) {
          setIbanMessage(
            isTitular
              ? labelsIbanRefunds.ibanRefunds_ibanDisabled_ibanRequired_owner
              : labelsIbanRefunds.ibanRefunds_ibanDisabled_ibanRequired_not_owner
          );
        } else if (
          labelsIbanRefunds.ibanRefunds_bankAccountDataHolder === 'INDIVIDUAL'
        ) {
          setIbanMessage(
            labelsIbanRefunds.ibanRefunds_ibanDisabled_ibanRequired_owner
          );
        }
      }
    }
  };

  useEffect(() => {
    if (formikContext && (formikContext?.dirty as any)) {
      (formikContext as any)?.validateForm((formikContext as any)?.values);
    }
  }, [formikContext]);

  useEffect(() => {
    setIbanUndefinedByDefault(!iban);
    defineIbanMessage();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div>
      <ChangeSuccessModal
        setShowModal={setStatusMessage}
        showModal={statusMessage}
      />
      <AppForm
        initialValues={{
          iBAN: iBAN || '',
          accountOwner: false,
        }}
        validationSchema={validationSchema}
        onChange={(val: any) => {
          val.iBAN = val.iBAN.replace(' ', '');
          setIban(val.iBAN);
          setValidIban(IBAN.isValid(iban));
        }}
        onSubmit={(values) => {
          if (!(values.accountOwner && validIban && ibanChanged)) {
            return;
          }
          handleSubmit();
          setJustSubmitted(true);
          setIbanChanged(false);
        }}>
        {!canSubmit &&
          !isTitular &&
          !ibanMessage &&
          labelsIbanRefunds.ibanRefunds_ibanIsEnabled === '1' && (
            <div
              className="iban-data-title"
              style={{...fonts.SM, ...fonts.SemiBold}}>
              {iBAN
                ? labels?.myData_cantSubmit_iBANEnd
                : labelsCreateRefund.refund_alert_not_owner}
            </div>
          )}
        <div>
          <div
            className={`my-data-iban-top-row${
              !editingIban ? ' read-only' : ''
            }`}
            style={!canSubmit ? {justifyContent: 'center'} : {}}>
            <div className="my-data-iban-column">
              <div className="iban-data-row-title">
                <p className="m-0" style={{...fonts.S}}>
                  {labels?.myData_iBAN}
                </p>
              </div>
              <div
                className="iban-data-row iban-data-row-edit iban-data-row-checkbox"
                style={{position: 'relative'}}>
                {editingIban ? (
                  <AppFormField
                    showErrorMessage
                    className="profile-input"
                    name="iBAN"
                    handleFormikContext={() => setFormikContext(formik)}
                    onMouseOver={(e) => defineOnMouseFunctions(e, 'iban')}
                    // error={
                    //   !IBAN.isValid(iban) && outOfIbanInput
                    //     ? 'iban Invalido'
                    //     : undefined
                    // } //o valor é irrelevante, apenas interessa ter alguma string não undefined para o icone aparecer.
                    disabled={
                      !canSubmit ||
                      (!editingIban && IBAN.isValid(iban)) ||
                      ibanUndefinedByDefault
                    }
                    onClick={() => {
                      canSubmit && !IBAN.isValid(iban) && setEditingIban(true);
                    }}
                    onInput={() => setIbanChanged(true)}
                  />
                ) : (
                  <p
                    style={{
                      ...fonts.M,
                      ...fonts.SemiBold,
                      color: colors.black,
                      paddingBottom: '20px',
                    }}>
                    {iban || '(nd)'}
                  </p>
                )}
              </div>
            </div>
            <div className="my-data-iban-column my-data-iban-checkbox">
              {editingIban && (
                <div className="iban-data-row" style={{marginBlock: 'auto'}}>
                  <AppFormCheckboxField
                    showErrorMessage
                    errorStyle={{marginLeft: '0'}}
                    name="accountOwner"
                    className="iban-data-checkbox"
                    labelClass="iban-data-label"
                    label={labels.myData_iBANCheckboxText}
                    handleFormikContext={() => setFormikContext(formik)}
                  />
                </div>
              )}
            </div>
            {canSubmit && (
              <div
                className="my-data-iban-bottom-row-mobile"
                style={{...fonts.SM}}>
                {labels?.myData_iBANEnd}
              </div>
            )}
          </div>
          <div className="my-data-iban-bottom-row">
            {canSubmit && (
              <div
                className="my-data-iban-bottom-row-desktop"
                style={{...fonts.SM}}>
                <p>{labels?.myData_iBANEnd}</p>
              </div>
            )}
            <span style={{...fonts.M}}>{ibanMessage}</span>
            {canSubmit && (
              <div style={{justifyContent: 'center'}}>
                {editingIban ? (
                  <AppFormSubmitButton
                    title={labels?.myData_iBANSave}
                    variant="default"
                    disabled={submitting}
                  />
                ) : (
                  <AppButton
                    variant="secondary"
                    onClick={() => {
                      setEditingIban(true);
                      justSubmitted && setJustSubmitted(false);
                      setIbanUndefinedByDefault(false);
                    }}>
                    {labels?.myData_iBANReimbursement_changeIBAN}
                  </AppButton>
                )}
              </div>
            )}
          </div>
        </div>
      </AppForm>
      <ModalTemplate
        type="error"
        show={errorIban}
        onHide={() => setErrorIban(false)}
        hideFooter={true}
        cross={true}>
        <p style={{color: colors.alertRed, fontWeight: 'bold'}}>
          {messageLabels?.general_errorMessage}
        </p>{' '}
      </ModalTemplate>
    </div>
  );
}

const mapStateToProps = ({
  strings,
  globalStyles,
  user,
  bankAccountDataHolder,
  selectedBu,
  titular,
}: any) => ({
  labels: strings.value.pt.myData,
  labelsCreateRefund: strings.value.pt.createRefund,
  labelsIbanRefunds: strings.value.pt.ibanRefunds,
  buStyles: globalStyles.value,
  messageLabels: strings.value.pt.messages,
  user: user.value,
  bankAccountDataHolder: bankAccountDataHolder.value,
  selectedBu: selectedBu.value,
  titular: titular.value,
});

export default compose(connect(mapStateToProps)(IbanFilter));
